import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, message, Table, Pagination, Tooltip, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, FilterOutlined } from '@ant-design/icons';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../assets/icons/ic-trash.svg?react';
import warningIcon from '../../../assets/icons/ic-warning.svg';
import CopyIcon from '../../../assets/icons/ic-git-compare.svg?react';
import styled from 'styled-components';
import ExportButton from '../../../components/common/ExportButton';
import ExportPanel from '../../../components/common/ExportPanel';
import type { ColumnsType } from 'antd/es/table';
import { RootState, AppDispatch } from '../../../store';
import AddItemButton from '../../../components/common/AddItemButton';

import { setCompany, setCompanies, setCurrentPage } from '../../../store/slices/companySlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { Company } from '../../../services/types';
import { duplicateCompany, getAllCompanies, getCompanyImageBase64, deleteCompany, updateCompany, createCompany } from '@/services/companyService';
import { getAnnualReports } from '@/services/financialStatementService';
import { setReports, setSelectedReport } from '@/store/slices/annualReportSlice';

import DuplicateCompanyDrawer from './DuplicateCompanyDrawer';
import {CompanyFormData} from './DuplicateCompanyDrawer';

import CreateCompanyDrawer from './CreateCompanyDrawer';

import EditCompanyDrawer from './EditCompanyDrawer';
import { CompanyFormData as EditCompanyFormData } from './EditCompanyDrawer';

import ConfirmModal from '../../../components/common/ConfirmModal';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;


const HeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButtonWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #E5E7EB;
    border-color: #D1D5DB;
  }

  &:active {
    background: #D1D5DB;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  .anticon {
    font-size: 18px;
    color: #6B7280;
  }
`;

const FilterBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #EF4444;
  border-radius: 50%;
`;

// const ColumnHeader = styled.div`
//   white-space: normal;
//   line-height: 1.4;
// `;

const TableContainer = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  
  .ant-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
  }
  
  .ant-table {
    font-size: 14px;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 14px;
    padding: 16px 12px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
    vertical-align: middle;
  
    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }

    /* Allow text wrap for specific long headers */
    &.wrap-header {
      white-space: normal;
      line-height: 1.4;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 20px 12px;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;
  
    &:last-child {
      border-right: none;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
  
  /* Remove selection highlight */
  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }
`;

const PaginationContainer = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-start;
  border-top: 1px solid #E5E7EB;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyStateMessage = styled.p`
  color: #6B7280;
  font-size: 16px;
  margin-bottom: 24px;
`;

// ==================== CHIP COMPONENTS ====================

const CompanyChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #DBEAFE;
  color: #2563EB;
  
  &::before {
    content: '●';
    font-size: 10px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const ActionMenuButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const DateText = styled.span`
  font-size: 14px;
  color: #1F2937;
`;


// ==================== ACTION MENU COMPONENTS ====================

// const ActionMenuButton = styled.button`
//   background: #F8FAFC;
//   border: none;
//   border-radius: 8px;
//   cursor: pointer;
//   padding: 8px;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   color: #6B7280;
//   transition: all 0.3s;

//   &:hover {
//     color: #1F2937;
//     background: #EFF6FF;
//   }

//   .anticon {
//     font-size: 20px;
//   }
// `;

// const MenuItemContent = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 8px;

//   svg {
//     flex-shrink: 0;
//     width: 16px;
//     height: 16px;
//   }
// `;

// const MenuItemText = styled.span<{ $danger?: boolean }>`
//   font-family: Inter;
//   font-weight: 500;
//   font-size: 14px;
//   line-height: 20px;
//   letter-spacing: 0.005em;
//   color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
// `;


// ==================== MAIN COMPONENT ====================

// Location state interface for navigation
interface LocationState {
  companyName: string | null;
  internalAdminName: string | null;
}

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  // Get filter params from navigation state
  const locationState = location.state as LocationState | null;
  const companyName = locationState?.companyName ?? null;
  const internalAdminName = locationState?.internalAdminName ?? null;

  const hasFilter = !!companyName || !!internalAdminName;

  // Get user data from Redux
  const { user } = useSelector((state: RootState) => state.auth);

  // Permission checks
  const isSuperAdmin = user?.userType === 'superAdministrator';
  const isExternalAdmin = user?.userType === 'externalAdministrator';

  const [isCompanyDrawerOpen, setIsCompanyDrawerOpen] = useState(false);
  const [selectedCompanyForDuplicate, setSelectedCompanyForDuplicate] = useState<Company | null>(null);

  const [isCreateCompanyDrawerOpen, setIsCreateCompanyDrawerOpen] = useState(false);

  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Company Edit modal state
  const [isEditCompanyDrawerOpen, setIsEditCompanyDrawerOpen] = useState(false);
  const [selectedCompanyForEdit, setSelectedCompanyForEdit] = useState<Company | null>(null);

  // Get user data from Redux
  //const { user } = useSelector((state: RootState) => state.auth);

  // Get companies data from Redux
  const {
    companies,
    totalCount,
    lastFetchedReportId,
    currentPage,
    pageSize
  } = useSelector((state: RootState) => state.company);

  // Permission checks
  //const isSuperAdmin = user?.userType === 'superAdministrators';
  //const isExternalAdmin = user?.userType === 'externalAdministrators';


  // Ref to track last fetched params to prevent duplicate API calls
  const lastFetchKeyRef = useRef<string | null>(null);

  // Fetch gaps when report changes or filter changes
  useEffect(() => {
    const fetchData = async () => {

      // Create a unique key for current fetch params
      const fetchKey = `companylist-${companyName || ''}-${internalAdminName || ''}`;
      
      // Skip if we already fetched with same params
      if (lastFetchKeyRef.current === fetchKey) {
        console.warn('⏭️ CompaniesPage: Skipping duplicate fetch for:', fetchKey);
        return;
      }

      // Skip if we already have data for this report and no filter is applied
    //   if (!hasFilter && lastFetchedReportId === 'companylist' && companies.length > 0) {
    //     console.warn('✅ Using cached gaps data');
    //     lastFetchKeyRef.current = fetchKey;
    //     return;
    //   }

      // Mark as fetching
      lastFetchKeyRef.current = fetchKey;
      setIsLoading(true);
      
      try {
        console.warn(`🔄 Fetching companies${hasFilter ? ` with filter (companyName: ${companyName}, interalAdminName: ${internalAdminName})` : ''}...`);
        const response = await getAllCompanies({
          companyName: companyName ?? undefined,
          internalAdminName: internalAdminName ?? undefined,
        });

        dispatch(setCompanies({
          data: response.data,
          totalCount: response.totalCount,
        }));

        console.warn(`✅ Fetched ${response.data.length} gaps`);
      } catch (error) {
        console.error('Error fetching gaps:', error);
        message.error('Failed to load gaps');
        // Reset fetch key on error to allow retry
        lastFetchKeyRef.current = null;
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [companies.length, dispatch, companyName, internalAdminName, hasFilter]);

  const reloadData = useCallback(async () => {
    try {
        console.warn(`🔄 Fetching companies${hasFilter ? ` with filter (companyName: ${companyName}, interalAdminName: ${internalAdminName})` : ''}...`);
        const response = await getAllCompanies({
          companyName: companyName ?? undefined,
          internalAdminName: internalAdminName ?? undefined,
        });

        dispatch(setCompanies({
          data: response.data,
          totalCount: response.totalCount,
        }));

        console.warn(`✅ Fetched ${response.data.length} gaps`);
      } catch (error) {
        console.error('Error fetching gaps:', error);
        message.error('Failed to load gaps');
        // Reset fetch key on error to allow retry
        lastFetchKeyRef.current = null;
      } finally {
        setIsLoading(false);
      }
  }, [dispatch])

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return companies.slice(start, end);
  }, [companies, currentPage, pageSize]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Navigation handlers
  const handleNavigateToCompany = async (company: Company) => {
    //debugger;
    localStorage.setItem("companyId", company.companyId);
    localStorage.setItem("companyName", company.companyName);

    var imageUrl = await getCompanyImageBase64(company.companyId);

    var imageBase64 = imageUrl.data.downloadUrl != null ? "data:image/png;base64," + imageUrl.data.downloadUrl : "";

    localStorage.setItem("companyImageUrl", imageBase64);

    const reports = await getAnnualReports(company.companyId);
    //setReports(reports);
    
    
    dispatch(setCompany({company}));
    dispatch(setReports( reports ));
    dispatch(setSelectedReport( reports[0] ));

    //setCompany( {company});
    navigate('/dashboard');
  };

  const handleOpenDuplicate = (company: Company) => {
    setSelectedCompanyForDuplicate(company);
    setIsCompanyDrawerOpen(true);
  };

  // Close description drawer
  const handleCloseDrawer = () => {
    setIsCompanyDrawerOpen(false); 
  };

  const handleCreateCompany = () => {
    setIsCreateCompanyDrawerOpen(true);
  };

  const handleDuplicateSubmit = async(formData: CompanyFormData) => {
    try {
      await duplicateCompany({
        companyId: formData.companyId,
        companyName: formData.companyName,
        annualReportId: formData.annualReportId
      })

      const response = await getAllCompanies({
          companyName: companyName ?? undefined,
          internalAdminName: internalAdminName ?? undefined,
        });

      dispatch(setCompanies({
        data: response.data,
        totalCount: response.totalCount,
      }));
    } catch (error) {
              const defaultErrorMessage = `Failed to duplicate company`;
              message.error(error instanceof Error ? error.message : defaultErrorMessage);
              throw error;
            }
  };

  const handleCreateSubmit = async(values: CompanyFormData) => {
    try {
      await createCompany({
        companyName: values.companyName
      })

      const response = await getAllCompanies({
          companyName: companyName ?? undefined,
          internalAdminName: internalAdminName ?? undefined,
        });

      dispatch(setCompanies({
        data: response.data,
        totalCount: response.totalCount,
      }));
    } catch (error) {
              const defaultErrorMessage = `Failed to create company`;
              message.error(error instanceof Error ? error.message : defaultErrorMessage);
              throw error;
            }
  };


   const handleDeleteCompany = (company: Company) => {
      setDeletingCompany(company);
      setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingCompany) {
          message.error('Company ID is required for delete');
          return;
        }
    
        setIsDeleting(true);
        try {
          await deleteCompany(deletingCompany.companyId);
          await reloadData();

          setDeleteModalOpen(false);
          setDeletingCompany(null);
          message.success('Company deleted successfully');
        } catch (error: any) {
          console.error('Failed to delete Company:', error);
          message.error(error.message || 'Failed to delete Company');
        } finally {
          setIsDeleting(false);
        }
      };
    
    const handleCancelDelete = () => {
      setDeleteModalOpen(false);
      setDeletingCompany(null);
    };

    const handleEditCompany = (company: Company) => {
      setSelectedCompanyForEdit(company);
      setIsEditCompanyDrawerOpen(true);
    };

    // Close description drawer
  const handleCloseCompany = () => {
    setIsEditCompanyDrawerOpen(false); 
    setIsCreateCompanyDrawerOpen(false);
  };

  const handleCompanySubmit = async(formData: EditCompanyFormData) => {
    try {
      await updateCompany({
        companyId: formData.companyId ?? "",
        companyName: formData.companyName ?? ""
      });

      reloadData();
    } catch (error) {
      const defaultErrorMessage = `Failed to update company`;
      message.error(error instanceof Error ? error.message : defaultErrorMessage);
      throw error;
    }
  }

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // Define columns
  const columns: ColumnsType<Company> = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 40,
      render: (status: string, record) => (
        <CompanyChip 
          $status={status} 
          onClick={() => handleNavigateToCompany(record)}
          style={{ cursor: 'pointer' }}
        >
          {status}
        </CompanyChip>
      ),
    },
    {
      title: 'Main Internal Admin',
      dataIndex: 'internalAdminName',
      key: 'internalAdminName',
      width: 40,
      
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 40,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
 
    {
          title: 'Action',
          key: 'actions',
          width: 15,
          fixed: 'right',
          align: 'center',
          render: (_, record) => {
            const menuItems: MenuProps['items'] = [
              {
                key: 'duplicate',
                label: (
                  <MenuItemContent>
                    <CopyIcon />
                    <MenuItemText>Duplicate</MenuItemText>
                  </MenuItemContent>
                ),
                onClick: () => handleOpenDuplicate(record),
                disabled: (() => {
                  // Super Admin can edit any status
                  if (isSuperAdmin || isExternalAdmin) return false;
                  // All other cases: disabled
                  return true;
                })(),
              },
              
            ];

            if (isSuperAdmin || isExternalAdmin) {
          menuItems.push(
            {
              type: 'divider',
            },
            {
              key: 'edit',
              label: (
                <MenuItemContent>
                  <EditIcon />
                  <MenuItemText>Edit</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleEditCompany(record)
            }
          );
        }

            if (isSuperAdmin || isExternalAdmin) {
          menuItems.push(
            {
              type: 'divider',
            },
            {
              key: 'delete',
              label:  (
                <MenuItemContent>
                  <TrashIcon />
                  <MenuItemText $danger>Delete</MenuItemText>
                </MenuItemContent>
              ),
              onClick: () => handleDeleteCompany(record)
            }
          );
        }
    
            const handleActionButtonClick = (e: React.MouseEvent) => {
              e.stopPropagation();
            };
    
            return (
              <div onClick={handleActionButtonClick}>
                <Dropdown
                  menu={{ items: menuItems }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <ActionMenuButton>
                    <MoreOutlined />
                  </ActionMenuButton>
                </Dropdown>
              </div>
            );
          },
        },
    
  ];

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };


  // Handle Export button click
  const handleOpenExportPanel = () => {
    setIsExportPanelOpen(true);
  };

  const handleCloseExportPanel = () => {
    setIsExportPanelOpen(false);
  };

  // Prepare export data with flattened nested objects
  const exportData = useMemo(() => {
    return companies.map(item => ({
      companyName: item.companyName,
      internalAdminName: item.internalAdminName,
      createdAt: item.createdAt
    }));
  }, [companies]);


//   // Reload data
//   const reloadData = useCallback(async () => {
//     if (!selectedReport?.annualReportId) return;

//     try {
//       const response = await getAllCompanies({
//         companyName: companyName ?? undefined,
//         internalAdminName: internalAdminName ?? undefined,
//       });
//       dispatch(setCompanies({
//         data: response.data,
//         totalCount: response.totalCount,
//       }));
//     } catch (error) {
//       console.error('Error refreshing gaps:', error);
//     }
//   }, [selectedReport, dispatch, companyName, internalAdminName]);

  // Handle clear filter - reset all filters to null and reload data
  const handleClearFilter = async () => {
 
    // Reset fetch key to allow useEffect to fetch fresh data
    lastFetchKeyRef.current = null;
    
    // Navigate first to clear the state
    navigate('/companies', { state: { processId: null, cycleId: null, status: null }, replace: true });

    // Fetch all gaps without filters
    setIsLoading(true);
    try {
      const response = await getAllCompanies({
      });
      dispatch(setCompanies({
        data: response.data,
        totalCount: response.totalCount,
      }));
      
      // Update fetch key after successful fetch
      lastFetchKeyRef.current = `companylist---`;
    } catch (error) {
      console.error('Error clearing filter:', error);
      message.error('Failed to clear filter');
    } finally {
      setIsLoading(false);
    }
  };

  // Action handlers


  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Company List</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading companies...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Empty state
  if (!isLoading && companies.length === 0 && lastFetchedReportId !== null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Company List</PageTitle>
        </PageHeader>
        <TableContainer>
          <EmptyStateContainer>
            <EmptyStateMessage>There are no companies to display yet.</EmptyStateMessage>
          </EmptyStateContainer>
        </TableContainer>
      </PageContainer>
    );
  }

  // Initial loading state
  if (companies.length === 0 && lastFetchedReportId === null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Company List</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading companies...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Company List</PageTitle>
        <HeaderButtonGroup>
          {isSuperAdmin ? <AddItemButton text="Create Company" onClick={handleCreateCompany} /> : ""}
          {hasFilter && (
            <Tooltip title="Clear filter">
              <FilterButtonWrapper onClick={handleClearFilter}>
                <FilterOutlined />
                <FilterBadge />
              </FilterButtonWrapper>
            </Tooltip>
          )}
          <ExportButton onClick={handleOpenExportPanel} title="Export" />
          
        </HeaderButtonGroup>
      </PageHeader>

      <TableContainer>
        <Table
          columns={columns}
          dataSource={paginatedData}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
          size="middle"
        />

        <PaginationContainer>
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={() => `${currentPage} of ${totalPages}`}
          />
        </PaginationContainer>
      </TableContainer>

      {/* Duplicate Company Drawer */}
      <DuplicateCompanyDrawer
        open={isCompanyDrawerOpen}
        initialValues={selectedCompanyForDuplicate || undefined}
        
        onClose={handleCloseDrawer}
        onSubmit={handleDuplicateSubmit}
      />

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'companyName', title: 'Company Name' },
          { key: 'internalAdminName', title: 'Main Internal Admin' },
          { key: 'createdAt', title: 'Created At' },
          
        ]}
        fileName="Companies_Report"
        title="Companies Report"
      />

{/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title={
          'Delete Company'
        }
        description={
          deletingCompany ? (
             (
              // Draft control - standard confirmation
              `Are you sure you want to delete company "${deletingCompany.companyName}"?`
            ) 
          ) : ''
        }
        icon={warningIcon}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        danger
      />


      {/* Cycle Drawer */}
      <EditCompanyDrawer
        open={isEditCompanyDrawerOpen}
        initialValues={selectedCompanyForEdit || undefined}
        
        onClose={handleCloseCompany}
        onSubmit={handleCompanySubmit}
      />

      {/* Duplicate Company Drawer */}
      <CreateCompanyDrawer
        open={isCreateCompanyDrawerOpen}
        initialValues={undefined}
        
        onClose={handleCloseCompany}
        onSubmit={handleCreateSubmit}
      />
    </PageContainer>
  );
};

export default CompaniesPage;
